/**
 * Override for form-engine-portable's "inline_group" field type, registered
 * via the `fieldComponents` extension point (same mechanism as
 * InfoFieldWithTemplateDownload — BOP PR closing the 7 Reference Template
 * format gaps vs. the real SWT "Proof of Student Status" form). form-engine
 * has no built-in renderer for this type at all: it's a sentence with
 * `{key}`-placeholder blanks spliced in (e.g. "I, {rep_name}, as
 * representative of the Overseas Recruiting Agency {agency_name},
 * certify..."), authored via shared/referenceTemplateFormAdapter.ts's
 * "inline_group" case in BackOfficePrototype.
 *
 * Each embedded blank is registered under ITS OWN top-level form field name
 * (the placeholder key, e.g. "rep_name") — NOT nested under the inline_group
 * question's own key — so the answer lands exactly where
 * referenceLetterPdf.ts and the answer-validation path already expect any
 * other question's answer to live.
 *
 * BackOfficePrototype's staff Preview dialog registers the identical
 * override — keep the two in sync if this changes.
 */
interface InlineGroupFieldValue {
  key: string;
  label: string;
  width?: number;
}

interface InlineGroupFieldProps {
  field: {
    template?: string;
    fields?: InlineGroupFieldValue[];
    label?: string;
  };
  form: {
    register: (name: string) => Record<string, unknown>;
    getValues: (name: string) => unknown;
  };
  disabled?: boolean;
  isReadOnly?: boolean;
}

type TemplatePart = { text: string } | { key: string };

function splitInlineTemplate(template: string): TemplatePart[] {
  const parts: TemplatePart[] = [];
  const re = /\{([a-zA-Z0-9_]+)\}/g;
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(template))) {
    if (m.index > lastIndex) parts.push({ text: template.slice(lastIndex, m.index) });
    parts.push({ key: m[1] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < template.length) parts.push({ text: template.slice(lastIndex) });
  return parts;
}

export function InlineGroupField({ field, form, disabled, isReadOnly }: InlineGroupFieldProps) {
  const parts = splitInlineTemplate(field.template ?? '');
  const fieldsByKey = new Map((field.fields ?? []).map((f) => [f.key, f]));
  const readOnly = Boolean(disabled || isReadOnly);

  return (
    <div className="mb-4">
      {field.label && <div className="mb-2 text-sm font-medium">{field.label}</div>}
      <p className="text-sm leading-relaxed">
        {parts.map((part, idx) =>
          'text' in part ? (
            <span key={idx}>{part.text}</span>
          ) : (
            <input
              key={idx}
              type="text"
              {...form.register(part.key)}
              defaultValue={(form.getValues(part.key) as string) ?? ''}
              disabled={readOnly}
              readOnly={readOnly}
              aria-label={fieldsByKey.get(part.key)?.label ?? part.key}
              className="mx-1 inline-block h-7 rounded border border-input bg-background px-2 text-sm"
              style={{ width: `${Math.max(8, fieldsByKey.get(part.key)?.width ?? 16)}ch` }}
            />
          ),
        )}
      </p>
    </div>
  );
}
